const PROXY_FUNCTION = "ga4-proxy"
const EDIT_SCOPE = "https://www.googleapis.com/auth/analytics.edit"
export const ANALYTICS_READ_SCOPE =
  "https://www.googleapis.com/auth/analytics.readonly"
export const ANALYTICS_MANAGE_USERS_SCOPE =
  "https://www.googleapis.com/auth/analytics.manage.users.readonly"
const ANALYTICS_MANAGE_USERS_FULL_SCOPE =
  "https://www.googleapis.com/auth/analytics.manage.users"
export const REQUIRED_ANALYTICS_SCOPES = [
  ANALYTICS_READ_SCOPE,
  ANALYTICS_MANAGE_USERS_SCOPE,
]
const EDIT_TOKEN_STORAGE_KEY = "ga4_edit_token_cache_v1"
let inMemoryEditToken = null

const isMissingApiKeyError = (error) =>
  typeof error?.message === "string" &&
  error.message.toLowerCase().includes("no api key found")

const nowIsoString = () => new Date().toISOString()

const isExpiredIso = (iso, bufferMs = 60000) => {
  if (!iso) return false
  const expiresAt = new Date(iso)
  if (Number.isNaN(expiresAt.getTime())) return false
  return expiresAt.getTime() - bufferMs <= Date.now()
}


const CIPHERTEXT_PREFIX = "U2FsdGVkX1"

const unwrapToken = (value, label = "token") => {
  if (!value || typeof value !== "string") return value || null
  if (value.startsWith(CIPHERTEXT_PREFIX)) {
    console.warn(
      `[GA4] Ignoring encrypted ${label} in browser; re-fetch it from the GA4 proxy`
    )
    return null
  }
  return value
}

const persistEditTokenRecord = (record) => {
  inMemoryEditToken = record ? { ...record } : null
  if (typeof window === "undefined" || !window?.localStorage) return
  try {
    if (!record) {
      window.localStorage.removeItem(EDIT_TOKEN_STORAGE_KEY)
      return
    }
    window.localStorage.setItem(
      EDIT_TOKEN_STORAGE_KEY,
      JSON.stringify(record)
    )
  } catch (err) {
    console.warn("[GA4] Failed to persist edit token locally", err)
  }
}

const readEditTokenRecord = () => {
  let payload = inMemoryEditToken
  if (!payload && typeof window !== "undefined" && window?.localStorage) {
    try {
      const raw = window.localStorage.getItem(EDIT_TOKEN_STORAGE_KEY)
      payload = raw ? JSON.parse(raw) : null
    } catch (err) {
      console.warn("[GA4] Failed to load cached edit token", err)
      payload = null
    }
  }
  if (!payload) return null
  const token = unwrapToken(payload.token, "stored GA4 edit token")
  return {
    token,
    expiresAt: payload.expiresAt || null,
    updatedAt: payload.updatedAt || null,
    userId: payload.userId || null,
    scope: payload.scope || null,
  }
}

const clearCachedEditToken = () => {
  inMemoryEditToken = null
  if (typeof window !== "undefined" && window?.localStorage) {
    try {
      window.localStorage.removeItem(EDIT_TOKEN_STORAGE_KEY)
    } catch (err) {
      console.warn("[GA4] Failed to clear GA4 edit token cache", err)
    }
  }
}

const persistPlainEditToken = (token, expiresAt, userId, updatedAt) => {
  if (!token || typeof token !== "string") {
    clearCachedEditToken()
    return null
  }
  const normalizedUpdatedAt = updatedAt || nowIsoString()
  const storedRecord = {
    token,
    expiresAt: expiresAt || null,
    updatedAt: normalizedUpdatedAt,
    userId: userId || null,
    scope: EDIT_SCOPE,
  }
  persistEditTokenRecord(storedRecord)
  return {
    token,
    expiresAt: storedRecord.expiresAt,
    updatedAt: storedRecord.updatedAt,
    userId: storedRecord.userId,
  }
}

const logEditTokenPersistError = (error) => {
  if (!error) return
  console.warn("[GA4] Failed to persist GA4 edit token", error)
}

const normalizeExpiresAt = (value, fallback = null) => {
  if (value === undefined) return fallback
  if (!value) return null
  return value
}

export const saveEditToken = async (supabase, userId, tokens = {}) => {
  const hasRefreshToken = Object.prototype.hasOwnProperty.call(
    tokens,
    "refresh_token"
  )
  if (hasRefreshToken && tokens.refresh_token) {
    console.warn("[GA4] Ignoring unexpected GA4 edit refresh token payload")
  }

  const hasAccessToken = Object.prototype.hasOwnProperty.call(
    tokens,
    "access_token"
  )
  const hasExpiresAt = Object.prototype.hasOwnProperty.call(
    tokens,
    "expires_at"
  )

  const existing = readEditTokenRecord()
  const accessTokenValue = hasAccessToken ? tokens.access_token : undefined
  const expiresAtValue = normalizeExpiresAt(
    hasExpiresAt ? tokens.expires_at : existing?.expiresAt,
    existing?.expiresAt || null
  )

  const updateTimestamp = nowIsoString()

  if (userId && supabase?.functions?.invoke) {
    const body = {
      type: "store-edit-token",
      userId,
      updatedAt: updateTimestamp,
    }
    if (hasAccessToken) body.token = accessTokenValue || null
    if (hasExpiresAt) body.expiresAt = expiresAtValue || null
    try {
      const { error } = await supabase.functions.invoke(PROXY_FUNCTION, {
        body,
      })
      if (error) {
        logEditTokenPersistError(error)
      }
    } catch (err) {
      logEditTokenPersistError(err)
    }
  }

  if (!hasAccessToken) return null

  return persistPlainEditToken(accessTokenValue, expiresAtValue, userId, updateTimestamp)
}

export const loadEditToken = async (supabase, userId) => {
  if (!userId || !supabase?.functions?.invoke) return null

  try {
    const { data, error } = await supabase.functions.invoke(PROXY_FUNCTION, {
      body: { type: "load-edit-token", userId },
    })

    if (error) {
      console.warn("[GA4] Failed to load GA4 edit token", { userId, error })
      return null
    }

    const record = data?.record
    if (!record?.token) {
      clearCachedEditToken()
      return null
    }

    return persistPlainEditToken(
      record.token,
      normalizeExpiresAt(record.expiresAt, null),
      userId,
      record.updatedAt || null
    )
  } catch (err) {
    console.warn("[GA4] Failed to load GA4 edit token", { userId, error: err })
    return null
  }
}

const normalizeTokenForProxy = (value) => {
  if (value === undefined) return undefined
  if (!value || typeof value !== "string") return null
  if (value.startsWith(CIPHERTEXT_PREFIX)) return null
  return value
}

export const saveTokens = async (supabase, userId, tokens = {}) => {
  if (!supabase?.functions?.invoke || !userId) return
  const tokenPayload = {
    access_token: normalizeTokenForProxy(tokens.access_token) ?? null,
  }
  if (Object.prototype.hasOwnProperty.call(tokens, "refresh_token")) {
    tokenPayload.refresh_token =
      normalizeTokenForProxy(tokens.refresh_token) ?? null
  }
  if (Object.prototype.hasOwnProperty.call(tokens, "expires_at")) {
    tokenPayload.expires_at = tokens.expires_at || null
  }
  try {
    const { error } = await supabase.functions.invoke(PROXY_FUNCTION, {
      body: {
        type: "store-oauth-tokens",
        userId,
        tokens: tokenPayload,
      },
    })
    if (error) {
      console.warn("[GA4] Failed to persist GA4 OAuth token via proxy", error)
    }
  } catch (err) {
    console.warn("[GA4] Proxy token persist threw", err)
  }
}

export const loadTokens = async (supabase, userId) => {
  const logLoadTokens = (stage, details = {}) => {
    if (typeof console === "undefined") return
    const payload = { userId, ...details }
    if (typeof console.debug === "function") {
      console.debug(`[GA4] loadTokens ${stage}`, payload)
    } else {
      console.log(`[GA4] loadTokens ${stage}`, payload)
    }
  }

  if (!supabase?.functions?.invoke || !userId) {
    logLoadTokens("skipped", { reason: "missing proxy client" })
    return {}
  }

  try {
    const { data, error } = await supabase.functions.invoke(PROXY_FUNCTION, {
      body: { type: "load-oauth-tokens", userId },
    })

    if (error) {
      console.warn("[GA4] Proxy token load failed", error)
      logLoadTokens("error", { message: error.message || null })
      return {}
    }

    const tokens = data?.tokens
    if (!tokens) {
      logLoadTokens("empty", {})
      return {}
    }

    logLoadTokens("return", {
      hasAccessToken: Boolean(tokens.access_token),
      hasRefreshToken: Object.prototype.hasOwnProperty.call(
        tokens,
        "refresh_token"
      )
        ? Boolean(tokens.refresh_token)
        : undefined,
      expiresAt: tokens.expires_at || null,
      updatedAt: tokens.updated_at || null,
    })

    return tokens
  } catch (err) {
    console.warn("[GA4] Proxy token load threw", err)
    logLoadTokens("error", { message: err.message || null })
    return {}
  }
}

const refreshAccessTokenViaProxy = async (supabase, refresh_token) => {
  if (!supabase?.functions?.invoke) return null
  try {
    const { data, error } = await supabase.functions.invoke("ga4-proxy", {
      body: { type: "refresh-access-token", refreshToken: refresh_token },
    })
    if (error) {
      console.warn("[GA4] Proxy refresh token exchange failed", error)
      return null
    }
    return data || null
  } catch (e) {
    console.warn("[GA4] Proxy refresh token exchange threw", e)
    return null
  }
}

const refreshAccessToken = async (supabase, refresh_token) => {
  return refreshAccessTokenViaProxy(supabase, refresh_token)
}

const normalizeRequiredScopes = (value) => {
  if (!value) return []
  if (Array.isArray(value)) {
    return value.filter((scope) => typeof scope === "string" && scope)
  }
  return typeof value === "string" && value
    ? [value]
    : []
}

const validateAccessToken = async (token, requiredScope) => {
  if (!token) return false
  const requiredScopes = normalizeRequiredScopes(requiredScope)
  try {
    const res = await fetch(
      `https://oauth2.googleapis.com/tokeninfo?access_token=${encodeURIComponent(
        token
      )}`
    )
    if (!res.ok) return false
    if (!requiredScopes.length) return true
    try {
      const body = await res.json()
      const scopeField =
        typeof body?.scope === "string" ? body.scope.trim() : ""
      const scopes = scopeField ? scopeField.split(/\s+/g) : []
      if (!scopes.length) return false
      const scopeSet = new Set(scopes)
      return requiredScopes.every((scope) => {
        if (scopeSet.has(scope)) return true
        if (
          scope === ANALYTICS_MANAGE_USERS_SCOPE &&
          scopeSet.has(ANALYTICS_MANAGE_USERS_FULL_SCOPE)
        ) {
          return true
        }
        return false
      })
    } catch (err) {
      console.warn("[GA4] Unable to inspect token scopes", err)
      return false
    }
  } catch (e) {
    console.warn("[GA4] Token validation failed", e)
    return false
  }
}

export const tokenHasRequiredAnalyticsScopes = async (
  token,
  requiredScope = REQUIRED_ANALYTICS_SCOPES,
) => {
  try {
    return await validateAccessToken(token, requiredScope)
  } catch (err) {
    console.warn("[GA4] Failed to verify GA4 token scopes", err)
    return false
  }
}

export const getStoredGa4EditToken = (userId) => {
  const record = readEditTokenRecord()
  if (!record) return null
  if (record.scope && record.scope !== EDIT_SCOPE) {
    clearCachedEditToken()
    return null
  }
  if (!record.token) {
    clearCachedEditToken()
    return null
  }
  if (userId && record.userId && record.userId !== userId) return null
  if (isExpiredIso(record.expiresAt)) {
    clearCachedEditToken()
    return null
  }
  return {
    token: record.token,
    expiresAt: record.expiresAt || null,
    updatedAt: record.updatedAt || null,
    userId: record.userId || null,
  }
}

export const isGa4EditTokenValid = (record) =>
  Boolean(record?.token) && !isExpiredIso(record?.expiresAt)

export const clearStoredGa4EditToken = () => {
  clearCachedEditToken()
}

export const syncGa4EditTokenFromSession = async (supabase, userId) => {
  if (!supabase?.auth?.getSession) return null
  try {
    const { data, error } = await supabase.auth.getSession()
    if (error) {
      if (!isMissingApiKeyError(error)) {
        console.warn("[GA4] Supabase getSession error", error)
      }
      return null
    }
    const session = data?.session
    const sessionToken = unwrapToken(
      session?.provider_token,
      "session access token"
    )
    if (!sessionToken) return null
    const hasScope = await validateAccessToken(sessionToken, EDIT_SCOPE)
    if (!hasScope) return null
    const expiresAt = session?.expires_at
      ? new Date(session.expires_at * 1000).toISOString()
      : null
    return saveEditToken(supabase, userId, {
      access_token: sessionToken,
      expires_at: expiresAt,
    })
  } catch (err) {
    if (!isMissingApiKeyError(err)) {
      console.warn("[GA4] Failed to sync GA4 edit token", err)
    }
    return null
  }
}

export const requestGa4EditToken = async (supabase, options = {}) => {
  if (!supabase?.auth?.signInWithOAuth) {
    return {
      data: null,
      error: new Error("Supabase auth is not available"),
      popup: null,
      popupBlocked: false,
      url: null,
    }
  }

  const {
    redirectTo =
      typeof window !== "undefined" && window?.location
        ? window.location.href
        : undefined,
    skipBrowserRedirect = true,
    openPopup = true,
    queryParams: extraQueryParams = {},
  } = options

  const queryParams = { ...extraQueryParams }
  if ("access_type" in queryParams && queryParams.access_type !== "online") {
    console.warn(
      "[GA4] Ignoring offline access_type override for edit token request"
    )
  }
  queryParams.access_type = "online"
  if (options.prompt) queryParams.prompt = options.prompt

  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        scopes: EDIT_SCOPE,
        redirectTo,
        skipBrowserRedirect,
        queryParams,
      },
    })

    let popup = null
    let popupBlocked = false
    const authUrl = data?.url || null
    if (!error && authUrl && openPopup && typeof window !== "undefined") {
      popup = window.open(authUrl, "ga4oauth", "width=500,height=600")
      if (!popup) popupBlocked = true
      else popup.focus?.()
    }

    return { data, error, popup, popupBlocked, url: authUrl }
  } catch (err) {
    return { data: null, error: err, popup: null, popupBlocked: false, url: null }
  }
}


export const getValidAccessToken = async (supabase, user, options = {}) => {
  const {
    persist = true,
    forceRefresh = false,
    targetUserId: explicitTargetUserId = null,
    requiredScopes = REQUIRED_ANALYTICS_SCOPES,
  } = options || {}

  const targetUserId = explicitTargetUserId || user?.id || null
  if (!targetUserId) return null

  const canUseSession = Boolean(user?.id) && user.id === targetUserId

  const resolveSession = async () => {
    if (!canUseSession || !supabase?.auth?.getSession) return null
    try {
      const { data, error } = await supabase.auth.getSession()
      if (error && !isMissingApiKeyError(error)) {
        console.warn("[GA4] Supabase getSession error", error)
      }
      return data?.session || null
    } catch (e) {
      if (!isMissingApiKeyError(e)) {
        console.warn("[GA4] Supabase getSession threw", e)
      }
      return null
    }
  }

  const refreshSession = async () => {
    if (!canUseSession || !supabase?.auth?.refreshSession) return null
    try {
      const { data, error } = await supabase.auth.refreshSession()
      if (error && !isMissingApiKeyError(error)) {
        console.warn("[GA4] Supabase refreshSession error", error)
      }
      return data?.session || null
    } catch (e) {
      if (!isMissingApiKeyError(e)) {
        console.warn("[GA4] Supabase refreshSession threw", e)
      }
      return null
    }
  }

  const persistTokens = async (token, refresh, expires) => {
    if (!persist || !targetUserId) return
    const payload = {
      access_token: token,
      expires_at: expires || null,
    }
    if (refresh !== undefined) payload.refresh_token = refresh
    await saveTokens(supabase, targetUserId, payload)
  }

  const ensureStoredAccessToken = async (enforceRefresh = forceRefresh) => {
    if (!persist) return null
    const stored = await loadTokens(supabase, targetUserId)
    if (typeof console !== "undefined") {
      const summary = {
        userId: targetUserId,
        hasAccessToken: Boolean(stored.access_token),
        hasRefreshToken: Object.prototype.hasOwnProperty.call(
          stored,
          "refresh_token",
        )
          ? Boolean(stored.refresh_token)
          : undefined,
        expiresAt: stored.expires_at || null,
      }
      if (typeof console.debug === "function") {
        console.debug("[GA4] ensureAccessToken loadTokens result", summary)
      } else {
        console.log("[GA4] ensureAccessToken loadTokens result", summary)
      }
    }
    if (
      stored.access_token &&
      !enforceRefresh &&
      (!stored.expires_at || new Date(stored.expires_at) > new Date()) &&
      (await validateAccessToken(stored.access_token, requiredScopes))
    ) {
      return stored.access_token
    }
    if (stored.refresh_token) {
      try {
        const refreshed = await refreshAccessToken(
          supabase,
          stored.refresh_token,
        )
        if (refreshed?.access_token) {
          const expires = refreshed.expires_in
            ? new Date(Date.now() + refreshed.expires_in * 1000).toISOString()
            : null
          const refreshedRefreshToken = unwrapToken(
            refreshed.refresh_token,
            "refreshed stored refresh token",
          )
          const nextRefresh =
            refreshedRefreshToken || stored.refresh_token || undefined
          const hasScopes = await validateAccessToken(
            refreshed.access_token,
            requiredScopes,
          )
          if (!hasScopes) {
            console.warn(
              "[GA4] Refreshed access token missing required scopes",
              {
                userId: targetUserId,
                hasStoredRefreshToken: Boolean(stored.refresh_token),
              },
            )
            if (persist && targetUserId) {
              const tokensToPersist = {
                access_token: null,
                expires_at: null,
              }
              if (nextRefresh !== undefined) {
                tokensToPersist.refresh_token = nextRefresh || null
              }
              await saveTokens(supabase, targetUserId, tokensToPersist)
            }
            return null
          }
          await persistTokens(refreshed.access_token, nextRefresh, expires)
          return refreshed.access_token
        }
      } catch (e) {
        console.warn("[GA4] Refresh token failed", {
          userId: targetUserId,
          hasStoredRefreshToken: Boolean(stored.refresh_token),
          error: e,
        })
      }
    }
    return null
  }

  if (canUseSession) {
    let session = await resolveSession()
    const shouldRefreshSession =
      forceRefresh ||
      !session?.provider_token ||
      (session?.expires_at && session.expires_at * 1000 < Date.now())

    let sessionRefreshToken = null
    const updateSessionRefreshToken = () => {
      sessionRefreshToken = unwrapToken(
        session?.provider_refresh_token,
        "session refresh token",
      )
    }

    updateSessionRefreshToken()

    const refreshSupabaseSession = async () => {
      const refreshed = await refreshSession()
      if (refreshed?.provider_token) {
        session = refreshed
        updateSessionRefreshToken()
      }
    }

    if (shouldRefreshSession) {
      await refreshSupabaseSession()
    }

    const resolveSessionToken = async (allowForce = false) => {
      const sessionToken = unwrapToken(
        session?.provider_token,
        "session access token",
      )
      if (!sessionToken) return null
      if (forceRefresh && !allowForce) return null
      if (!(await validateAccessToken(sessionToken, requiredScopes))) {
        return null
      }
      const expires = session?.expires_at
        ? new Date(session.expires_at * 1000).toISOString()
        : null
      const refreshValue =
        sessionRefreshToken !== null
          ? sessionRefreshToken || undefined
          : undefined
      await persistTokens(sessionToken, refreshValue, expires)
      return sessionToken
    }

    let token = await resolveSessionToken(false)
    if (token) return token

    if (shouldRefreshSession) {
      token = await resolveSessionToken(true)
      if (token) return token
    }

    const refreshWithSessionToken = async () => {
      if (!persist || !sessionRefreshToken) return null
      try {
        const refreshed = await refreshAccessToken(
          supabase,
          sessionRefreshToken,
        )
        if (refreshed?.access_token) {
          const expires = refreshed.expires_in
            ? new Date(Date.now() + refreshed.expires_in * 1000).toISOString()
            : null
          const refreshedRefreshToken = unwrapToken(
            refreshed.refresh_token,
            "refreshed session refresh token",
          )
          const nextRefresh =
            refreshedRefreshToken || sessionRefreshToken || undefined
          if (refreshedRefreshToken) {
            sessionRefreshToken = refreshedRefreshToken
          }
          const hasScopes = await validateAccessToken(
            refreshed.access_token,
            requiredScopes,
          )
          if (!hasScopes) {
            console.warn(
              "[GA4] Session refresh token produced access token missing required scopes",
              {
                userId: targetUserId,
                hasSessionRefreshToken: Boolean(sessionRefreshToken),
              },
            )
            if (persist && targetUserId) {
              const tokensToPersist = {
                access_token: null,
                expires_at: null,
              }
              if (nextRefresh !== undefined) {
                tokensToPersist.refresh_token = nextRefresh || null
              }
              await saveTokens(supabase, targetUserId, tokensToPersist)
            }
            return null
          }
          await persistTokens(refreshed.access_token, nextRefresh, expires)
          return refreshed.access_token
        }
      } catch (e) {
        console.warn("[GA4] Session refresh token exchange failed", e)
      }
      return null
    }

    token = await refreshWithSessionToken()
    if (token) return token
  }

  return ensureStoredAccessToken(forceRefresh)
}
